import { useSyncExternalStore } from "react";
import { APP_GENERATION } from "./generation";

/**
 * Shared update state. Lives outside React because two writers sit on
 * different sides of the React tree: the version poller hook and the
 * `vite:preloadError` handler installed in main.tsx before the app mounts.
 *
 * - `available`: a newer build is deployed; the banner can be snoozed.
 * - `required`: this tab is too old to keep working reliably (the deployed
 *   generation is higher, or a lazy chunk of this build is gone). The banner
 *   cannot be snoozed, but the page is still never reloaded mid-input.
 */
export type UpdateStatus = "current" | "available" | "required";

export interface UpdateState {
  status: UpdateStatus;
  /** Build id of the newer deploy, when version.json reported one. */
  remoteBuildId: string | null;
  /** True while an `available` banner is snoozed (cleared after SNOOZE_MS). */
  snoozed: boolean;
}

export interface RemoteVersion {
  buildId: string;
  /** Null when the deployed version.json predates the generation field. */
  generation: number | null;
}

export const SNOOZE_MS = 30 * 60 * 1000;

const INITIAL_STATE: UpdateState = {
  status: "current",
  remoteBuildId: null,
  snoozed: false,
};

let state: UpdateState = INITIAL_STATE;
const listeners = new Set<() => void>();
let snoozeTimer: number | undefined;

function setState(next: UpdateState): void {
  state = next;
  for (const listener of listeners) listener();
}

export function getUpdateState(): UpdateState {
  return state;
}

export function subscribeToUpdateState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Record what /version.json reported. A matching build id means this tab is
 * current again (for example after a rollback deploy), so any pending
 * prompt is cleared. Otherwise `required` is sticky: a later deploy with the
 * same generation does not fix a tab that already lost a chunk or fell a
 * generation behind.
 */
export function reportRemoteVersion(
  remote: RemoteVersion,
  baselineBuildId: string,
): void {
  if (remote.buildId === baselineBuildId) {
    if (state.status !== "current") setState({ ...state, status: "current", remoteBuildId: null });
    return;
  }
  const status: UpdateStatus =
    state.status === "required" ||
      (remote.generation !== null && remote.generation > APP_GENERATION)
      ? "required"
      : "available";
  if (status === state.status && remote.buildId === state.remoteBuildId) return;
  setState({ ...state, status, remoteBuildId: remote.buildId });
}

/** A lazy chunk of the running build could not be loaded. */
export function markUpdateRequired(): void {
  if (state.status === "required") return;
  setState({ ...state, status: "required" });
}

/** Hide an `available` banner for SNOOZE_MS; it then comes back by itself. */
export function snoozeUpdate(): void {
  clearTimeout(snoozeTimer);
  snoozeTimer = window.setTimeout(() => {
    snoozeTimer = undefined;
    setState({ ...state, snoozed: false });
  }, SNOOZE_MS);
  setState({ ...state, snoozed: true });
}

export function isBannerVisible(current: UpdateState): boolean {
  if (current.status === "required") return true;
  return current.status === "available" && !current.snoozed;
}

export function useAppUpdateState(): UpdateState {
  return useSyncExternalStore(subscribeToUpdateState, getUpdateState);
}

/** Test seam: module state otherwise leaks between test cases. */
export function resetUpdateState(): void {
  clearTimeout(snoozeTimer);
  snoozeTimer = undefined;
  setState(INITIAL_STATE);
}
